'use client'

'use client'

import { useTransition } from "react"
import { Button } from "../buttons"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../forms"
import { Input } from "../forms/Input";
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import Link from "next/link"
import { signIn } from "next-auth/react"
import { useRouter } from "next/navigation"
import { useToast } from "../../hooks";
import { signup } from "@shared/actions"
import { Logger } from "@shared/logger"

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters.",
  })
})

/**
 * @group Interfaces
 * 
 * @summary
 * <blockquote>Props for the AuthForm component that handles both sign-in and sign-up functionality.</blockquote>
 * 
 * @example
 * ```typescript
 * const props: AuthFormProps = {
 *   mode: 'signup',
 *   useServerAction: true
 * };
 * ```
 */
interface AuthFormProps {
  mode: 'signin' | 'signup'
  useServerAction?: boolean
}

/**
 * `AuthForm` provides a reusable authentication form for signing in or signing up users.
 *
 * - Validates email and password using Zod and react-hook-form.
 * - On successful signup, automatically signs the user in.
 * - Displays form errors, toast notifications, and loading indicators.
 * - Uses API calls for authentication (server actions are handled via API routes).
 *
 * @param mode - Choose between `'signin'` and `'signup'` behavior.
 * @param useServerAction - Deprecated: All authentication now uses API calls for client-side compatibility.
 */
export function AuthForm({ mode, useServerAction = false }: AuthFormProps) {
  const [isPending, startTransition] = useTransition()
  const router = useRouter()
  const { toast } = useToast()
  const logger = new Logger()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  /**
   * Handles signup using API call
   * Server actions are accessed through API routes for client-side compatibility
   */
  async function handleSignupWithAPI(values: z.infer<typeof formSchema>): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      })
      const data = await response.json()

      if (!response.ok) {
        toast({
          title: "Sign up failed",
          description: data.error || 'Something went wrong during sign up',
          variant: "destructive",
        })
        return false
      }
      return true
    } catch (error) {
      logger.error('Signup API error:', 'auth.signup', undefined, error instanceof Error ? error : new Error(String(error)))
      toast({
        title: "Sign up failed",
        description: 'Network error occurred during sign up',
        variant: "destructive",
      })
      return false
    }
  }

  /**
   * Handles automatic sign in after successful signup
   */
  async function handleAutoSignInAfterSignup(values: z.infer<typeof formSchema>): Promise<void> {
    const result = await signIn('credentials', {
      redirect: false,
      email: values.email,
      password: values.password,
    })

    if (result?.error) {
      toast({
        title: "Sign in failed",
        description: result.error || 'Failed to sign in after registration',
        variant: "destructive",
      })
    } else {
      router.push('/dashboard')
    }
  }

  /**
   * Handles user sign in
   */
  async function handleSignIn(values: z.infer<typeof formSchema>): Promise<void> {
    const result = await signIn('credentials', {
      redirect: false,
      email: values.email,
      password: values.password,
    })

    if (result?.error) {
      handleSignInError(result.error)
    } else {
      router.push('/dashboard')
    }
  }

  /**
   * Handles sign in error messages
   */
  function handleSignInError(error: string): void {
    let errorMessage = 'Invalid email or password'

    // Map common errors to user-friendly messages
    switch (error) {
      case 'CredentialsSignin':
        errorMessage = 'The email or password you entered is incorrect'
        break
      case 'EmailNotVerified':
        errorMessage = 'Please verify your email address before signing in'
        break
      case 'AccountDisabled':
        errorMessage = 'Your account has been disabled. Please contact support'
        break
      default:
        errorMessage = 'Unable to sign in. Please try again'
    }

    toast({
      title: "Authentication Failed",
      description: errorMessage,
      variant: "destructive",
    })
  }

  /**
   * Handles the complete signup process
   * All signup operations now use API calls for client-side compatibility
   */
  async function handleSignup(values: z.infer<typeof formSchema>): Promise<void> {
    const signupSuccess = await handleSignupWithAPI(values)

    if (signupSuccess) {
      await handleAutoSignInAfterSignup(values)
    }
  }

  /**
   * Handles form submission for both sign-in and sign-up modes.
   */
  function onSubmit(values: z.infer<typeof formSchema>) {
    startTransition(async () => {
      try {
        if (mode === 'signup') {
          await handleSignup(values)
        } else {
          await handleSignIn(values)
        }
      } catch (error) {
        logger.error('Authentication error', 'auth.signup', undefined, error instanceof Error ? error : new Error(String(error)))
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          variant: "destructive",
        })
      }
    })
  }

  return (
    <div className="w-full max-w-[400px] space-y-8 px-4 sm:px-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="name@example.com" 
                    type="email" 
                    disabled={isPending}
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter your password" 
                    type="password" 
                    disabled={isPending}
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isPending}
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                <span>{mode === 'signin' ? 'Signing in...' : 'Creating account...'}</span>
              </div>
            ) : (
              mode === 'signin' ? 'Sign In' : 'Sign Up'
            )}
          </Button>
        </form>
      </Form>

      <div className="text-center text-sm">
        {mode === 'signin' ? (
          <>
            Don&apos;t have an account?{" "}
            <Link href="/users/signup" className="underline underline-offset-4 hover:text-primary">
              Sign up
            </Link>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <Link href="/users/signin" className="underline underline-offset-4 hover:text-primary">
              Sign in
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
